import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import './support.css';
import apiService from './services/api';
import {
  CLIENT_CLOSE_ACTION,
  CLIENT_CLOSE_BODY,
  CLIENT_CLOSE_CANCEL,
  CLIENT_CLOSE_TITLE,
  CLIENT_LEVEL_SUPPORT_CONTEXT,
  CLOSED_CASE_CTA,
  CLOSED_CASE_NOTICE,
  CSAT_FEEDBACK_PROMPT,
  CSAT_QUESTION,
  CSAT_SCALE,
  NEW_CASE_MIN_CHARS,
  NEW_CASE_MIN_CHARS_ERROR,
  SUPPORT_LIST_POLL_MS,
  SUPPORT_THREAD_POLL_MS,
  clientClosedByLabel,
  formatStatusLabel,
  formatSupportWhen,
  isClientClosed,
  isOpenStatus,
  isClosedStatus,
  sortTicketsForTab,
  statusBadgeClass,
  supportCaseContextLabel,
  ticketClosedAt,
  ticketClosureNote,
} from './supportTicketPresentation';
import { loadImpersonationSession } from './supportMode/supportModeSession';
import { resolveImpersonationSupportTicketId } from './supportMode/supportCaseDeepLink';
import { buildBrandManagerCards } from './lib/brandManagerStore';

function unwrapData(payload) {
  return payload?.data ?? payload;
}

/**
 * Client Support.
 *
 * Two canonical contexts (SUPPORT-QA-01):
 *   - no `brandContext` → Client / Brand Manager hub: account-level Cases plus
 *     Cases for every Brand the user is authorized for (server-filtered).
 *   - `brandContext={{ brandId, brandName }}` → Brand-scoped Support: only that
 *     Brand's Cases are listed/opened; new Cases persist that Brand context.
 *     Every API call carries the Brand context so the server enforces it.
 */
/** `?csatRating=N` (1–5) from the Case-closed email; null otherwise. */
export function emailCsatRating(params) {
  const n = Number(params?.get?.('csatRating'));
  return Number.isInteger(n) && n >= 1 && n <= 5 ? n : null;
}

/** Landing came from the Case-closed email survey (either param form). */
export function isEmailCsatLanding(params) {
  return Boolean(params?.get?.('csat')) || emailCsatRating(params) != null;
}

const Support = ({ brandContext = null }) => {
  const location = useLocation();
  const scopedBrandId = String(brandContext?.brandId || '').trim() || null;
  const scopedBrandName =
    String(brandContext?.brandName || '').trim() || scopedBrandId;
  const isBrandScoped = Boolean(scopedBrandId);
  const [message, setMessage] = useState('');
  const [activeTab, setActiveTab] = useState('all'); // all | open | closed
  const [brandFilter, setBrandFilter] = useState('all'); // all | client | brandId
  const [createBrandId, setCreateBrandId] = useState('');
  const [accessibleBrands, setAccessibleBrands] = useState([]);
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [submitNotice, setSubmitNotice] = useState(null);
  const [expandedTicketId, setExpandedTicketId] = useState(null);
  const [deepLinkTicketId, setDeepLinkTicketId] = useState(null);
  const [threadByTicket, setThreadByTicket] = useState({});
  const [threadDraft, setThreadDraft] = useState({});
  const [threadLoading, setThreadLoading] = useState({});
  const [threadError, setThreadError] = useState({});
  const [threadSending, setThreadSending] = useState({});
  // Closed-Case CSAT: server response per Case (null = not yet submitted).
  const [csatByTicket, setCsatByTicket] = useState({});
  const [csatDraft, setCsatDraft] = useState({}); // { [ticketId]: { rating, feedbackText } }
  const [csatSending, setCsatSending] = useState({});
  const [csatError, setCsatError] = useState({});
  // Client-initiated close (Owner decision): confirmation target + in-flight.
  const [closeConfirmTicketId, setCloseConfirmTicketId] = useState(null);
  const [closingTicket, setClosingTicket] = useState({});
  const [closeError, setCloseError] = useState(null);
  const autoOpenedTicketRef = useRef(null);
  const deepLinkCardRef = useRef(null);
  const composerRef = useRef(null);

  useEffect(() => {
    if (location.state?.prefillMessage) {
      setMessage(location.state.prefillMessage);
    }
  }, [location.state]);

  // `silent` = background refresh (polling / focus): never flashes the
  // "Loading cases…" placeholder or clears a visible error.
  const loadTickets = useCallback(async ({ silent = false } = {}) => {
    if (!silent) {
      setLoading(true);
      setError(null);
    }
    try {
      const raw = isBrandScoped
        ? await apiService.listSupportTickets({ brandId: scopedBrandId })
        : await apiService.listSupportTickets();
      const list =
        raw?.data?.supportTickets ||
        raw?.supportTickets ||
        [];
      const rows = Array.isArray(list) ? list : [];
      // Defense-in-depth only — the API already scoped the list.
      setTickets(
        isBrandScoped
          ? rows.filter(
              (t) => String(t?.brandId || '').trim() === scopedBrandId,
            )
          : rows,
      );
    } catch (err) {
      if (silent) {
        // Keep the last good list on a transient background failure.
        console.warn('Support list background refresh failed', err);
        return;
      }
      console.error('Failed to load support tickets', err);
      setError(err?.message || 'Failed to load support tickets');
      setTickets([]);
    } finally {
      if (!silent) setLoading(false);
    }
  }, [isBrandScoped, scopedBrandId]);

  const loadAccessibleBrands = useCallback(async () => {
    if (isBrandScoped) {
      // Inside a Brand there is exactly one context; never offer other Brands.
      setAccessibleBrands([]);
      return;
    }
    try {
      const [mineRaw, catalogRaw] = await Promise.all([
        apiService.listMyBrands().catch(() => ({ brandMemberships: [] })),
        apiService.listBrandCatalog().catch(() => ({ brands: [] })),
      ]);
      const mine = unwrapData(mineRaw);
      const catalog = unwrapData(catalogRaw);
      const cards = buildBrandManagerCards({
        catalogBrands: catalog?.brands || catalog?.items || [],
        memberships: mine?.brandMemberships || [],
      });
      setAccessibleBrands(
        cards.map((c) => ({ brandId: c.id, brandName: c.name })),
      );
    } catch (err) {
      console.warn('Failed to load brands for Support context', err);
      setAccessibleBrands([]);
    }
  }, [isBrandScoped]);

  useEffect(() => {
    loadTickets();
    loadAccessibleBrands();
  }, [loadTickets, loadAccessibleBrands]);

  const loadThread = useCallback(async (ticketId, { silent = false } = {}) => {
    if (!silent) {
      setThreadLoading((prev) => ({ ...prev, [ticketId]: true }));
      setThreadError((prev) => ({ ...prev, [ticketId]: null }));
    }
    try {
      const raw = isBrandScoped
        ? await apiService.listSupportMessages(ticketId, {
            brandId: scopedBrandId,
          })
        : await apiService.listSupportMessages(ticketId);
      const messages =
        raw?.data?.messages || raw?.messages || [];
      setThreadByTicket((prev) => ({
        ...prev,
        [ticketId]: Array.isArray(messages) ? messages : [],
      }));
      // Closed Cases carry the Client's CSAT response (or null) with the thread.
      const data = raw?.data ?? raw;
      if (data && Object.prototype.hasOwnProperty.call(data, 'csat')) {
        setCsatByTicket((prev) => ({ ...prev, [ticketId]: data.csat || null }));
      }
    } catch (err) {
      if (silent) return; // keep the last good thread on a background failure
      setThreadError((prev) => ({
        ...prev,
        [ticketId]: err?.message || 'Failed to load conversation',
      }));
    } finally {
      if (!silent) setThreadLoading((prev) => ({ ...prev, [ticketId]: false }));
    }
  }, [isBrandScoped, scopedBrandId]);

  // -------------------------------------------------------------------------
  // Live state synchronization (Owner QA FIX 4). MA had NO refresh mechanism:
  // a Case closed in SA stayed "Open" here until a browser reload. Smallest
  // canonical mechanism: lightweight polling of the already-used list/thread
  // calls while the page is visible, plus an immediate refresh when the tab
  // regains focus/visibility. No websocket / new subsystem.
  // -------------------------------------------------------------------------
  const expandedRef = useRef(null);
  expandedRef.current = expandedTicketId;
  const ticketsRef = useRef([]);
  ticketsRef.current = tickets;
  const csatDoneRef = useRef({});
  useEffect(() => {
    const done = {};
    for (const t of tickets) {
      const c = csatByTicket[t.ticketId];
      done[t.ticketId] =
        Boolean(c?.rating) || (c === undefined && Boolean(t.csatRating));
    }
    csatDoneRef.current = done;
  }, [tickets, csatByTicket]);

  const refreshLive = useCallback(() => {
    if (typeof document !== 'undefined' && document.visibilityState === 'hidden') {
      return;
    }
    void loadTickets({ silent: true });
    const open = expandedRef.current;
    if (!open) return;
    const t = ticketsRef.current.find((x) => x.ticketId === open);
    // Expanded conversation: new messages while Open; while Closed, the CSAT
    // response until it is recorded — after that the Case is static.
    const settled = t && isClosedStatus(t.status) && csatDoneRef.current[open];
    if (!settled) void loadThread(open, { silent: true });
  }, [loadTickets, loadThread]);

  useEffect(() => {
    if (loading) return undefined;
    const id = setInterval(refreshLive, SUPPORT_LIST_POLL_MS);
    const onVisible = () => {
      if (document.visibilityState === 'visible') refreshLive();
    };
    document.addEventListener('visibilitychange', onVisible);
    window.addEventListener('focus', refreshLive);
    return () => {
      clearInterval(id);
      document.removeEventListener('visibilitychange', onVisible);
      window.removeEventListener('focus', refreshLive);
    };
  }, [loading, refreshLive]);

  // A list refresh that flips an expanded Case Open → Closed (or shows a CSAT
  // recorded elsewhere, e.g. from the email link) converges the thread now.
  const lastStatusRef = useRef({});
  useEffect(() => {
    const open = expandedRef.current;
    const next = {};
    for (const t of tickets) next[t.ticketId] = `${t.status}|${t.csatSubmittedAt || ''}`;
    if (open && lastStatusRef.current[open] && lastStatusRef.current[open] !== next[open]) {
      void loadThread(open, { silent: true });
    }
    lastStatusRef.current = next;
  }, [tickets, loadThread]);

  // Impersonate into Case: open the canonical conversation from session
  // and/or ?openTicket= query (bootstrap appends this for Support landings).
  // Does not create/reopen cases — only expands an already-listed ticket.
  useEffect(() => {
    if (loading || tickets.length === 0) return;
    const session = loadImpersonationSession();
    const params = new URLSearchParams(location.search || "");
    const fromQuery = String(params.get("openTicket") || "").trim();
    const targetId =
      fromQuery || resolveImpersonationSupportTicketId(session);
    if (!targetId || autoOpenedTicketRef.current === targetId) return;
    const match = tickets.find(
      (t) => String(t?.ticketId || "").trim() === targetId,
    );
    if (!match) return;
    autoOpenedTicketRef.current = targetId;
    setDeepLinkTicketId(targetId);
    setActiveTab(isOpenStatus(match.status) ? 'open' : 'closed');
    setExpandedTicketId(targetId);
    // Case-closed email links each CSAT face as `&csatRating=N`: preselect
    // that face so the Client only confirms. The rating is still submitted
    // through the canonical CSAT endpoint — never recorded from the URL alone.
    const preselect = emailCsatRating(params);
    if (preselect && !isOpenStatus(match.status)) {
      setCsatDraft((prev) => ({
        ...prev,
        [targetId]: { ...(prev[targetId] || {}), rating: preselect },
      }));
    }
    void loadThread(targetId);
  }, [loading, tickets, loadThread, location.search]);

  // After expand, bring the exact case into view (conversation already open).
  useEffect(() => {
    if (!deepLinkTicketId || expandedTicketId !== deepLinkTicketId) return;
    if (!deepLinkCardRef.current) return;
    deepLinkCardRef.current.scrollIntoView({
      behavior: 'smooth',
      block: 'start',
    });
  }, [deepLinkTicketId, expandedTicketId, activeTab, loading]);

  // Owner QA FIX 5: a too-short description must produce VISIBLE, field-level
  // feedback (not a page-level banner the Client can miss). The error clears
  // as soon as the input becomes valid; SEND is never a silent no-op.
  const [composerError, setComposerError] = useState(null);
  const composerErrorId = 'new-support-case-error';
  const summaryLength = message.trim().length;
  useEffect(() => {
    if (composerError && summaryLength >= NEW_CASE_MIN_CHARS) setComposerError(null);
  }, [composerError, summaryLength]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    const summary = message.trim();
    if (summary.length < NEW_CASE_MIN_CHARS) {
      setComposerError(NEW_CASE_MIN_CHARS_ERROR);
      composerRef.current?.focus?.();
      return;
    }
    setComposerError(null);
    setSubmitting(true);
    setError(null);
    setSubmitNotice(null);
    try {
      const raw = await apiService.createSupportTicket({
        summary,
        // Inside a Brand the context is fixed; the hub lets the user choose.
        brandId: isBrandScoped ? scopedBrandId : createBrandId || null,
      });
      const created =
        raw?.data?.supportTicket || raw?.supportTicket || null;
      setMessage('');
      setSubmitNotice(
        created?.ticketId
          ? `Support case ${created.ticketId} created.`
          : 'Support case created.',
      );
      setActiveTab('open');
      await loadTickets();
    } catch (err) {
      console.error('Failed to create support ticket', err);
      setError(err?.message || 'Failed to create support ticket');
    } finally {
      setSubmitting(false);
    }
  };

  const toggleThread = useCallback(
    async (ticketId) => {
      setExpandedTicketId((cur) => (cur === ticketId ? null : ticketId));
      if (expandedTicketId !== ticketId) {
        await loadThread(ticketId);
      }
    },
    [expandedTicketId, loadThread],
  );

  const sendThreadReply = useCallback(
    async (ticketId) => {
      const bodyText = String(threadDraft[ticketId] || '').trim();
      if (!bodyText) return;
      // Closed is final — never POST into a Closed Case (server also rejects).
      const target = tickets.find((t) => t.ticketId === ticketId);
      if (target && isClosedStatus(target.status)) {
        setThreadError((prev) => ({
          ...prev,
          [ticketId]: `${CLOSED_CASE_NOTICE} ${CLOSED_CASE_CTA}`,
        }));
        return;
      }
      setThreadSending((prev) => ({ ...prev, [ticketId]: true }));
      setThreadError((prev) => ({ ...prev, [ticketId]: null }));
      try {
        await apiService.postSupportMessage(
          ticketId,
          isBrandScoped
            ? { bodyText, brandId: scopedBrandId }
            : { bodyText },
        );
        setThreadDraft((prev) => ({ ...prev, [ticketId]: '' }));
        await loadThread(ticketId);
        await loadTickets();
      } catch (err) {
        setThreadError((prev) => ({
          ...prev,
          [ticketId]: err?.message || 'Failed to send reply',
        }));
        // A 409 means the Case was closed meanwhile — refresh to show closure UX.
        if (err?.status === 409 || /closed/i.test(String(err?.message || ''))) {
          void loadTickets();
        }
      } finally {
        setThreadSending((prev) => ({ ...prev, [ticketId]: false }));
      }
    },
    [threadDraft, tickets, loadThread, loadTickets, isBrandScoped, scopedBrandId],
  );

  // -------------------------------------------------------------------------
  // Client closes their OWN Open Case (Owner decision). Confirmation first;
  // then the SAME canonical Closed lifecycle as a staff close: the Case stays
  // readable, the composer disappears, CSAT + "Open a new Support Case" appear,
  // Open/Closed/All counts update. No closure note is collected or sent.
  // -------------------------------------------------------------------------
  const requestCloseCase = useCallback((ticketId) => {
    setCloseError(null);
    setCloseConfirmTicketId(ticketId);
  }, []);

  const cancelCloseCase = useCallback(() => {
    setCloseConfirmTicketId(null);
    setCloseError(null);
  }, []);

  const confirmCloseCase = useCallback(async () => {
    const ticketId = closeConfirmTicketId;
    if (!ticketId) return;
    const target = tickets.find((t) => t.ticketId === ticketId);
    if (target && isClosedStatus(target.status)) {
      // Already closed (e.g. staff closed it meanwhile) — nothing to do.
      setCloseConfirmTicketId(null);
      void loadTickets({ silent: true });
      return;
    }
    setClosingTicket((prev) => ({ ...prev, [ticketId]: true }));
    setCloseError(null);
    try {
      const raw = await apiService.closeSupportTicket(
        ticketId,
        isBrandScoped ? { brandId: scopedBrandId } : {},
      );
      const closed = raw?.data?.supportTicket || raw?.supportTicket || null;
      if (closed?.ticketId) {
        // Immediate local convergence; the list refresh below is authoritative.
        setTickets((prev) =>
          prev.map((t) => (t.ticketId === closed.ticketId ? { ...t, ...closed } : t)),
        );
      }
      setCloseConfirmTicketId(null);
      setThreadDraft((prev) => ({ ...prev, [ticketId]: '' }));
      setThreadError((prev) => ({ ...prev, [ticketId]: null }));
      await loadTickets({ silent: true });
      if (expandedTicketId === ticketId) {
        await loadThread(ticketId, { silent: true });
      }
    } catch (err) {
      if (err?.status === 409 || /already closed|is closed/i.test(String(err?.message || ''))) {
        // Closed meanwhile — converge to the closed UX rather than erroring.
        setCloseConfirmTicketId(null);
        void loadTickets({ silent: true });
        return;
      }
      setCloseError(err?.message || 'Failed to close the Support Case');
    } finally {
      setClosingTicket((prev) => ({ ...prev, [ticketId]: false }));
    }
  }, [
    closeConfirmTicketId,
    tickets,
    isBrandScoped,
    scopedBrandId,
    loadTickets,
    loadThread,
    expandedTicketId,
  ]);

  // Escape closes the confirmation (never confirms).
  useEffect(() => {
    if (!closeConfirmTicketId) return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape') cancelCloseCase();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [closeConfirmTicketId, cancelCloseCase]);

  // "Open a new Support Case" — reuse the canonical creation form above.
  const focusNewCaseComposer = useCallback(() => {
    if (composerRef.current) {
      composerRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
      composerRef.current.focus();
    }
  }, []);

  // Closed-case emails link "Open a new Support Case" as `?newCase=1` → land
  // on the canonical creation form (no parallel mechanism). Once per landing.
  const newCaseFocusedRef = useRef(false);
  useEffect(() => {
    if (loading || newCaseFocusedRef.current) return;
    const params = new URLSearchParams(location.search || '');
    if (params.get('newCase') !== '1') return;
    newCaseFocusedRef.current = true;
    focusNewCaseComposer();
  }, [loading, location.search, focusNewCaseComposer]);

  const submitCsat = useCallback(
    async (ticketId) => {
      const draft = csatDraft[ticketId] || {};
      const rating = Number(draft.rating);
      if (!Number.isInteger(rating) || rating < 1 || rating > 5) {
        setCsatError((prev) => ({
          ...prev,
          [ticketId]: 'Please choose a rating from 1 to 5.',
        }));
        return;
      }
      setCsatSending((prev) => ({ ...prev, [ticketId]: true }));
      setCsatError((prev) => ({ ...prev, [ticketId]: null }));
      try {
        const params = new URLSearchParams(location.search || '');
        const raw = await apiService.submitSupportCsat(ticketId, {
          rating,
          feedbackText: draft.feedbackText || null,
          brandId: isBrandScoped ? scopedBrandId : null,
          via: isEmailCsatLanding(params) ? 'email-link' : 'ma-ui',
        });
        const csat = raw?.data?.csat || raw?.csat || { rating };
        setCsatByTicket((prev) => ({ ...prev, [ticketId]: csat }));
        setCsatDraft((prev) => ({ ...prev, [ticketId]: {} }));
      } catch (err) {
        // Already submitted elsewhere (e.g. from the email) → show thanks state.
        if (err?.status === 409 || /already/i.test(String(err?.message || ''))) {
          void loadThread(ticketId);
        }
        setCsatError((prev) => ({
          ...prev,
          [ticketId]: err?.message || 'Failed to submit feedback',
        }));
      } finally {
        setCsatSending((prev) => ({ ...prev, [ticketId]: false }));
      }
    },
    [csatDraft, isBrandScoped, scopedBrandId, loadThread, location.search],
  );

  const allCount = tickets.length;
  const openCount = useMemo(
    () => tickets.filter((t) => isOpenStatus(t.status)).length,
    [tickets],
  );
  const closedCount = useMemo(
    () => tickets.filter((t) => isClosedStatus(t.status)).length,
    [tickets],
  );

  const brandFilterOptions = useMemo(() => {
    const fromTickets = [];
    const seen = new Set();
    for (const t of tickets) {
      const id = String(t?.brandId || "").trim();
      if (!id || seen.has(id)) continue;
      seen.add(id);
      fromTickets.push({
        brandId: id,
        brandName: supportCaseContextLabel(t),
      });
    }
    for (const b of accessibleBrands) {
      if (seen.has(b.brandId)) continue;
      seen.add(b.brandId);
      fromTickets.push(b);
    }
    return fromTickets.sort((a, b) =>
      String(a.brandName).localeCompare(String(b.brandName)),
    );
  }, [tickets, accessibleBrands]);

  const showBrandControls =
    !isBrandScoped &&
    (accessibleBrands.length > 0 || brandFilterOptions.length > 0);

  const filteredTickets = useMemo(() => {
    const tabFiltered = tickets.filter((ticket) => {
      if (activeTab === 'open') return isOpenStatus(ticket.status);
      if (activeTab === 'closed') return isClosedStatus(ticket.status);
      return true;
    });
    const brandFiltered = tabFiltered.filter((ticket) => {
      if (brandFilter === 'all') return true;
      if (brandFilter === 'client') {
        return !String(ticket?.brandId || "").trim();
      }
      return String(ticket?.brandId || "").trim() === brandFilter;
    });
    // Owner QA FIX 6 / Round 2: canonical per-tab ordering ONLY (Closed →
    // closedAt DESC; Open/All → most recently active first), deterministic
    // tie-breaks; "Case: NN" never drives position. A deep-linked Case
    // (?openTicket= / Support Mode bound Case) is expanded and scrolled into
    // view — it is NEVER hoisted above more recently closed Cases (that pin
    // made KK-294667, closed 9/19, sit above 9/23 closures in MA Closed).
    return sortTicketsForTab(brandFiltered, activeTab);
  }, [tickets, activeTab, brandFilter]);

  return (
    <>
      {isBrandScoped ? (
        <>
          <h1 data-testid="support-brand-heading">{scopedBrandName} Support</h1>
          <p
            className="message-instruction"
            data-testid="support-brand-context"
            data-brand-id={scopedBrandId}
          >
            Support cases for {scopedBrandName} only. New requests are filed
            under {scopedBrandName}.
          </p>
        </>
      ) : (
        <>
          <h1>Support</h1>
          <p className="message-instruction">
            Your Client Support Hub — cases for this Master Account, including
            Client / Brand Manager and Brand-specific requests.
          </p>
        </>
      )}
      <div className="support-content">
        <div className="message-section">
          {error && <div className="support-error">{error}</div>}
          {submitNotice && <div className="support-notice">{submitNotice}</div>}
          {showBrandControls ? (
            <label className="support-context-label">
              Case context
              <select
                className="support-select"
                value={createBrandId}
                onChange={(e) => setCreateBrandId(e.target.value)}
                aria-label="Support case brand context"
              >
                <option value="">{CLIENT_LEVEL_SUPPORT_CONTEXT}</option>
                {accessibleBrands.map((b) => (
                  <option key={b.brandId} value={b.brandId}>
                    {b.brandName}
                  </option>
                ))}
              </select>
            </label>
          ) : null}
          <textarea
            ref={composerRef}
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            rows={6}
            className={`message-input${composerError ? ' message-input-invalid' : ''}`}
            placeholder={`Describe your issue (at least ${NEW_CASE_MIN_CHARS} characters)...`}
            aria-label="New support case description"
            aria-invalid={composerError ? 'true' : undefined}
            aria-describedby={composerError ? composerErrorId : undefined}
            data-testid="new-support-case-input"
          />
          {composerError ? (
            <div
              id={composerErrorId}
              className="support-field-error"
              role="alert"
              data-testid="new-support-case-error"
            >
              {composerError}
              <span className="support-field-error-count">
                {' '}
                ({summaryLength}/{NEW_CASE_MIN_CHARS})
              </span>
            </div>
          ) : null}
          <button
            type="button"
            onClick={handleSubmit}
            className="send-button"
            disabled={submitting}
            data-testid="new-support-case-send"
          >
            {submitting ? 'SENDING…' : 'SEND'}
          </button>
        </div>

        <div className="tickets-section">
          <h2>Support Cases</h2>
          <div
            className="ticket-tabs"
            role="tablist"
            aria-label="Support case lists"
          >
            <button
              type="button"
              role="tab"
              aria-selected={activeTab === 'all'}
              id="support-tab-all"
              className={`tab-button ${activeTab === 'all' ? 'active' : ''}`}
              onClick={() => setActiveTab('all')}
            >
              All
              <span className="tab-count" aria-hidden="true">
                ({loading ? '…' : allCount})
              </span>
            </button>
            <button
              type="button"
              role="tab"
              aria-selected={activeTab === 'open'}
              id="support-tab-open"
              className={`tab-button ${activeTab === 'open' ? 'active' : ''}`}
              onClick={() => setActiveTab('open')}
            >
              Open
              <span className="tab-count" aria-hidden="true">
                ({loading ? '…' : openCount})
              </span>
            </button>
            <button
              type="button"
              role="tab"
              aria-selected={activeTab === 'closed'}
              id="support-tab-closed"
              className={`tab-button ${activeTab === 'closed' ? 'active' : ''}`}
              onClick={() => setActiveTab('closed')}
            >
              Closed
              <span className="tab-count" aria-hidden="true">
                ({loading ? '…' : closedCount})
              </span>
            </button>
          </div>

          {showBrandControls ? (
            <label className="support-context-label support-filter-label">
              Brand / context
              <select
                className="support-select"
                value={brandFilter}
                onChange={(e) => setBrandFilter(e.target.value)}
                aria-label="Filter support cases by brand context"
              >
                <option value="all">All Brands / Client Level</option>
                <option value="client">{CLIENT_LEVEL_SUPPORT_CONTEXT}</option>
                {brandFilterOptions.map((b) => (
                  <option key={b.brandId} value={b.brandId}>
                    {b.brandName}
                  </option>
                ))}
              </select>
            </label>
          ) : null}

          <div
            className="tickets-list"
            role="tabpanel"
            aria-labelledby={
              activeTab === 'all'
                ? 'support-tab-all'
                : activeTab === 'open'
                  ? 'support-tab-open'
                  : 'support-tab-closed'
            }
          >
            {loading ? (
              <div className="no-tickets">Loading cases…</div>
            ) : filteredTickets.length === 0 ? (
              <div className="no-tickets">
                No {activeTab === 'all' ? '' : `${activeTab} `}cases found
              </div>
            ) : (
              filteredTickets.map((ticket) => {
                const isClosed = isClosedStatus(ticket.status);
                const clientClosed = isClosed && isClientClosed(ticket);
                const closedBy = clientClosedByLabel(ticket);
                const closedWhen = formatSupportWhen(ticketClosedAt(ticket));
                const closureNote = ticketClosureNote(ticket);
                const csat = csatByTicket[ticket.ticketId];
                const csatDone =
                  Boolean(csat?.rating) ||
                  (csat === undefined && Boolean(ticket.csatRating));
                const draft = csatDraft[ticket.ticketId] || {};
                const isDeepLinkTarget =
                  deepLinkTicketId && ticket.ticketId === deepLinkTicketId;
                return (
                  <div
                    key={ticket.ticketId}
                    className="ticket-card"
                    ref={isDeepLinkTarget ? deepLinkCardRef : null}
                    data-testid={
                      isDeepLinkTarget
                        ? `support-deep-link-${ticket.ticketId}`
                        : `support-case-${ticket.ticketId}`
                    }
                    data-expanded={
                      expandedTicketId === ticket.ticketId ? "true" : "false"
                    }
                  >
                    <div className="ticket-header">
                      <div className="ticket-id-row">
                        <div>
                          <div className="ticket-case-seq">
                            Case:{" "}
                            {ticket.clientCaseLabel ||
                              (ticket.clientCaseNumber != null
                                ? String(ticket.clientCaseNumber).padStart(2, "0")
                                : "—")}
                          </div>
                          <div className="ticket-id">
                            Case Number: {ticket.ticketId}
                          </div>
                        </div>
                        <span className={statusBadgeClass(ticket.status)}>
                          {formatStatusLabel(ticket.status)}
                        </span>
                      </div>
                      <div className="ticket-date">
                        {isClosed && closedWhen
                          ? `Closed: ${closedWhen}`
                          : `Last Updated: ${formatSupportWhen(
                              ticket.updatedAt || ticket.createdAt,
                            )}`}
                      </div>
                    </div>
                    <div className="ticket-meta">
                      Brand/context: {supportCaseContextLabel(ticket)}
                    </div>
                    <div className="ticket-field">
                      <div className="ticket-field-label">Support request</div>
                      <div className="ticket-message">
                        {ticket.summary || '—'}
                      </div>
                    </div>
                    {isClosed ? (
                      <div
                        className="ticket-resolution"
                        data-testid={`support-closure-${ticket.ticketId}`}
                        data-closed-by-type={clientClosed ? 'client' : 'staff'}
                      >
                        {/* A Client-initiated closure has no staff Closure Note — never manufacture one. */}
                        {clientClosed ? null : (
                          <div className="ticket-field">
                            <div className="ticket-field-label">Closure note</div>
                            <div className="ticket-response">
                              {closureNote
                                ? closureNote
                                : 'No closure note provided.'}
                            </div>
                          </div>
                        )}
                        <div className="ticket-resolution-meta">
                          {closedBy ? (
                            <span>Closed by {closedBy}</span>
                          ) : null}
                          {closedWhen ? (
                            <span>Closed {closedWhen}</span>
                          ) : null}
                        </div>
                      </div>
                    ) : null}
                    <div className="ticket-actions">
                      <button
                        type="button"
                        className="tab-button"
                        onClick={() => void toggleThread(ticket.ticketId)}
                      >
                        {expandedTicketId === ticket.ticketId
                          ? 'Hide conversation'
                          : 'View conversation'}
                      </button>
                      {!isClosed ? (
                        <button
                          type="button"
                          className="support-secondary-button"
                          data-testid={`support-close-case-${ticket.ticketId}`}
                          disabled={Boolean(closingTicket[ticket.ticketId])}
                          onClick={() => requestCloseCase(ticket.ticketId)}
                        >
                          {closingTicket[ticket.ticketId]
                            ? 'Closing…'
                            : CLIENT_CLOSE_ACTION}
                        </button>
                      ) : null}
                    </div>
                    {expandedTicketId === ticket.ticketId ? (
                      <div className="ticket-thread">
                        {threadLoading[ticket.ticketId] ? (
                          <div className="no-tickets">Loading conversation…</div>
                        ) : null}
                        {threadError[ticket.ticketId] ? (
                          <div className="support-error">
                            {threadError[ticket.ticketId]}
                          </div>
                        ) : null}
                        <div className="ticket-thread-list">
                          {(threadByTicket[ticket.ticketId] || []).map((msg) => (
                            <div key={msg.messageId} className="ticket-thread-msg">
                              <div className="ticket-thread-meta">
                                <strong>
                                  {msg.authorType === 'sa'
                                    ? msg.authorDisplayName || 'KrossKast Support'
                                    : msg.authorType === 'email'
                                      ? msg.authorDisplayName || 'Email'
                                      : 'You'}
                                </strong>
                                <span>
                                  {formatSupportWhen(msg.createdAt)}
                                </span>
                              </div>
                              <div className="ticket-message">
                                {msg.bodyText || '—'}
                              </div>
                            </div>
                          ))}
                        </div>
                        {isClosed ? (
                          <>
                            <div
                              className="support-closed-notice"
                              data-testid={`support-closed-notice-${ticket.ticketId}`}
                              role="status"
                            >
                              <strong>{CLOSED_CASE_NOTICE}</strong>{' '}
                              <button
                                type="button"
                                className="support-link-button"
                                onClick={focusNewCaseComposer}
                              >
                                {CLOSED_CASE_CTA}
                              </button>
                            </div>
                            <div
                              className="support-csat"
                              data-testid={`support-csat-${ticket.ticketId}`}
                            >
                              {csatDone ? (
                                <div className="support-csat-thanks">
                                  Thanks for your feedback
                                  {csat?.rating
                                    ? ` (${csat.rating}/5)`
                                    : ticket.csatRating
                                      ? ` (${ticket.csatRating}/5)`
                                      : ''}
                                  .
                                </div>
                              ) : (
                                <fieldset
                                  className="support-csat-form"
                                  disabled={csatSending[ticket.ticketId]}
                                >
                                  <legend>{CSAT_QUESTION}</legend>
                                  <div
                                    className="support-csat-scale"
                                    role="radiogroup"
                                    aria-label={CSAT_QUESTION}
                                  >
                                    {CSAT_SCALE.map((opt) => (
                                      <label
                                        key={opt.value}
                                        className={`support-csat-option ${
                                          Number(draft.rating) === opt.value
                                            ? 'selected'
                                            : ''
                                        }`}
                                        title={opt.label}
                                      >
                                        <input
                                          type="radio"
                                          name={`csat-${ticket.ticketId}`}
                                          value={opt.value}
                                          checked={Number(draft.rating) === opt.value}
                                          onChange={() =>
                                            setCsatDraft((prev) => ({
                                              ...prev,
                                              [ticket.ticketId]: {
                                                ...(prev[ticket.ticketId] || {}),
                                                rating: opt.value,
                                              },
                                            }))
                                          }
                                          aria-label={`${opt.value} - ${opt.label}`}
                                        />
                                        <span className="support-csat-face" aria-hidden="true">
                                          {opt.face}
                                        </span>
                                        <span className="support-csat-value">
                                          {opt.value}
                                        </span>
                                      </label>
                                    ))}
                                  </div>
                                  <div
                                    className="support-csat-feedback"
                                    data-testid={`support-csat-feedback-${ticket.ticketId}`}
                                  >
                                    {/* Owner QA FIX 3: prompt ABOVE the field (explicit block order). */}
                                    <label
                                      className="support-csat-feedback-label"
                                      htmlFor={`csat-feedback-${ticket.ticketId}`}
                                    >
                                      {CSAT_FEEDBACK_PROMPT}
                                    </label>
                                    <textarea
                                      id={`csat-feedback-${ticket.ticketId}`}
                                      className="message-input support-csat-feedback-input"
                                      rows={2}
                                      maxLength={2000}
                                      value={draft.feedbackText || ''}
                                      onChange={(e) =>
                                        setCsatDraft((prev) => ({
                                          ...prev,
                                          [ticket.ticketId]: {
                                            ...(prev[ticket.ticketId] || {}),
                                            feedbackText: e.target.value,
                                          },
                                        }))
                                      }
                                      placeholder="Optional"
                                    />
                                  </div>
                                  {csatError[ticket.ticketId] ? (
                                    <div className="support-error">
                                      {csatError[ticket.ticketId]}
                                    </div>
                                  ) : null}
                                  <button
                                    type="button"
                                    className="send-button"
                                    disabled={
                                      csatSending[ticket.ticketId] || !draft.rating
                                    }
                                    onClick={() => void submitCsat(ticket.ticketId)}
                                  >
                                    {csatSending[ticket.ticketId]
                                      ? 'SUBMITTING…'
                                      : 'SUBMIT FEEDBACK'}
                                  </button>
                                </fieldset>
                              )}
                            </div>
                          </>
                        ) : (
                          <>
                            <textarea
                              className="message-input"
                              rows={3}
                              value={threadDraft[ticket.ticketId] || ''}
                              onChange={(e) =>
                                setThreadDraft((prev) => ({
                                  ...prev,
                                  [ticket.ticketId]: e.target.value,
                                }))
                              }
                              placeholder="Reply to this support case…"
                            />
                            <button
                              type="button"
                              className="send-button"
                              disabled={threadSending[ticket.ticketId]}
                              onClick={() => void sendThreadReply(ticket.ticketId)}
                            >
                              {threadSending[ticket.ticketId]
                                ? 'SENDING…'
                                : 'SEND REPLY'}
                            </button>
                          </>
                        )}
                      </div>
                    ) : null}
                  </div>
                );
              })
            )}
          </div>
        </div>
      </div>
      {closeConfirmTicketId ? (
        <div
          className="support-confirm-overlay"
          data-testid="support-close-confirm"
          onClick={(e) => {
            if (e.target === e.currentTarget) cancelCloseCase();
          }}
        >
          <div
            className="support-confirm-modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="support-close-confirm-title"
            aria-describedby="support-close-confirm-body"
          >
            <h2 id="support-close-confirm-title">{CLIENT_CLOSE_TITLE}</h2>
            <p className="support-confirm-case">
              Case Number: {closeConfirmTicketId}
            </p>
            <p id="support-close-confirm-body">{CLIENT_CLOSE_BODY}</p>
            {closeError ? (
              <div className="support-error" role="alert">
                {closeError}
              </div>
            ) : null}
            <div className="support-confirm-actions">
              <button
                type="button"
                className="support-secondary-button"
                onClick={cancelCloseCase}
                disabled={Boolean(closingTicket[closeConfirmTicketId])}
                autoFocus
              >
                {CLIENT_CLOSE_CANCEL}
              </button>
              <button
                type="button"
                className="send-button"
                data-testid="support-close-confirm-submit"
                onClick={() => void confirmCloseCase()}
                disabled={Boolean(closingTicket[closeConfirmTicketId])}
              >
                {closingTicket[closeConfirmTicketId]
                  ? 'Closing…'
                  : CLIENT_CLOSE_ACTION}
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </>
  );
};

export default Support;
